import { TriangleAlert } from "lucide-react";
import type { WriteRejection } from "@estelle/domain";

/**
 * One `WriteRejection`, rendered as what it actually is.
 *
 * Shared by `ContactDetail` (tags, custom fields, archive, delete) and
 * `ContactForm` (create and edit) rather than duplicated between them — both
 * write through the same `ContactsRepository` and so can hit the same five
 * rejection kinds.
 *
 * The switch has no `default` — a sixth rejection kind added to the union in
 * `@estelle/domain` fails this file at compile time instead of silently
 * falling through to a generic message nobody can act on. Contact writes
 * take no `Guarded`, so `conflict` cannot occur from anything in this module
 * today, but the union carries no way to say that, and handling every kind
 * here is cheaper than a bug report the day the interface changes to add one.
 */
export function RejectionNotice({ rejection }: { rejection: WriteRejection }) {
  switch (rejection.kind) {
    case "conflict":
      return (
        <p className="text-destructive flex items-start gap-1.5 text-xs">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {rejection.message} It changed at {rejection.currentUpdatedAt}.
        </p>
      );
    case "invalid":
      return (
        <p className="text-destructive text-xs">
          {rejection.message}
          {rejection.fields
            ? " " +
              Object.entries(rejection.fields)
                .map(([field, problem]) => `${field}: ${problem}`)
                .join("; ")
            : null}
        </p>
      );
    case "forbidden":
      return (
        <p className="text-destructive text-xs">
          {rejection.message} (requires {rejection.permission})
        </p>
      );
    case "blocked":
    case "channel_rejected":
      return <p className="text-destructive text-xs">{rejection.message}</p>;
  }
}
